import { syntaxConfig } from '../services/SyntaxConfigurationService';
import {
  bifrostCallArguments,
  isExcelError,
  matchesExcelResult,
  unionReferences,
  unionValue,
  type CellRef,
  type CellValue,
} from './cellUnionParsing';

/**
 * Rewrites `E8+E7`-style arguments into a union value, by reading the calling
 * cell's formula and the referenced cells.
 *
 * Every invocation that asks within one short window shares a single Excel.run:
 * one sync for the formulas, one for the referenced cells. Reading per cell
 * queued hundreds of round trips behind each other on a recalculation.
 *
 * A cell whose `cell+cell` argument cannot be read must not fall back to what
 * Excel passed — that is the sum, a different code — so it gets an error to
 * recalculate instead.
 */

const BATCH_WINDOW_MS = 25;
const READ_TIMEOUT_MS = 30000;

export type UnionOutcome =
  | { kind: 'unchanged' }
  | { kind: 'rewritten'; params: unknown[] }
  | { kind: 'unreadable' };

interface Pending {
  address: string;
  params: unknown[];
  resolve: (outcome: UnionOutcome) => void;
}

let queue: Pending[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

function splitAddress(address: string): { sheet: string; cell: string } | null {
  const bang = address.lastIndexOf('!');
  if (bang < 0) return null;
  const sheet = address.slice(0, bang);
  return {
    sheet: sheet.startsWith("'") ? sheet.slice(1, -1).replace(/''/g, "'") : sheet,
    cell: address.slice(bang + 1).replace(/\$/g, ''),
  };
}

const keyOf = (sheet: string, cell: string) => `${sheet}!${cell.toUpperCase()}`;

/** What one invocation becomes, given its formula and the values of every referenced cell. */
function outcomeFor(item: Pending, formula: string | undefined, values: Map<string, CellValue>): UnionOutcome {
  const at = splitAddress(item.address);
  if (!at || formula === undefined) return { kind: 'unreadable' };
  const calls = bifrostCallArguments(formula).map(args => args.map(unionReferences));
  if (!calls.some(call => call.some(Boolean))) return { kind: 'unchanged' };

  const operator = syntaxConfig.getAddOperator();
  for (const call of calls) {
    const rewritten = item.params.slice();
    let used = false;
    let consistent = true;
    call.forEach((refs, i) => {
      if (!refs || !consistent) return;
      const cells = refs.map(r => values.get(keyOf(r.sheet ?? at.sheet, r.cell)));
      if (!matchesExcelResult(item.params[i], cells)) { consistent = false; return; }
      const value = unionValue(cells, operator);
      if (value !== null) { rewritten[i] = value; used = true; }
    });
    if (consistent && used) return { kind: 'rewritten', params: rewritten };
  }
  // The formula joins cells with + but none of its calls matches what Excel
  // passed: the cells changed since, or the read is stale. Never guess.
  return { kind: 'unreadable' };
}

async function flush(): Promise<void> {
  const batch = queue;
  queue = [];
  timer = null;
  try {
    const outcomes = await Excel.run(async context => {
      const formulaRanges = batch.map(item => {
        const at = splitAddress(item.address);
        return at ? context.workbook.worksheets.getItem(at.sheet).getRange(at.cell).load('formulas') : null;
      });
      await context.sync();
      const formulas = formulaRanges.map(range => {
        const f = range?.formulas?.[0]?.[0];
        return typeof f === 'string' ? f : undefined;
      });

      const wanted = new Map<string, { sheet: string; cell: string }>();
      batch.forEach((item, i) => {
        const at = splitAddress(item.address);
        const formula = formulas[i];
        if (!at || !formula) return;
        for (const args of bifrostCallArguments(formula)) {
          for (const refs of args.map(unionReferences)) {
            refs?.forEach((r: CellRef) => wanted.set(keyOf(r.sheet ?? at.sheet, r.cell), { sheet: r.sheet ?? at.sheet, cell: r.cell }));
          }
        }
      });
      const valueRanges = Array.from(wanted.entries()).map(([key, r]) =>
        ({ key, range: context.workbook.worksheets.getItem(r.sheet).getRange(r.cell).load('values') }));
      if (valueRanges.length > 0) await context.sync();
      const values = new Map<string, CellValue>();
      valueRanges.forEach(({ key, range }) => values.set(key, range.values?.[0]?.[0] as CellValue));

      return batch.map((item, i) => outcomeFor(item, formulas[i], values));
    });
    batch.forEach((item, i) => item.resolve(outcomes[i]));
  } catch {
    batch.forEach(item => item.resolve({ kind: 'unreadable' }));
  }
}

/** Whether an invocation could hold an argument Excel computed from `cell+cell`. */
export function mayHoldCellUnion(params: unknown[]): boolean {
  return params.some(p => typeof p === 'number' || isExcelError(p));
}

/**
 * What the invocation's arguments are as written. Rewritten only when what Excel
 * passed is what it computes from the cells, which ties the formula to this call
 * even when one cell holds several BIFROST calls.
 */
export function resolveCellUnions(address: string, params: unknown[]): Promise<UnionOutcome> {
  const read = new Promise<UnionOutcome>(resolve => {
    queue.push({ address, params, resolve });
    if (!timer) timer = setTimeout(() => { void flush(); }, BATCH_WINDOW_MS);
  });
  // Whether Excel always answers a read during its own calculation is not
  // documented; a cell must not wait for ever.
  const giveUp = new Promise<UnionOutcome>(resolve =>
    setTimeout(() => resolve({ kind: 'unreadable' }), READ_TIMEOUT_MS));
  return Promise.race([read, giveUp]);
}
